/**
 * Display formatters for scope-projected profile values (SILL-359).
 *
 * The live pro adapter (`adapt.ts`) receives canonical *storage tokens* from the
 * marketplace projection — e.g. `timeline: "3_6_months"`, `buyer_type:
 * "first_time"`, `credit_score_range: "excellent_740+"` (see
 * backend/internal/services/onboarding/enums.go). Those are NOT display-ready.
 * The preview fixtures hand-author polished strings and bypass the adapter, so
 * only the live path leaked raw tokens into a professional-facing surface.
 *
 * These pure functions are the single humanization layer. Keep them free of
 * view-model / React imports so the rest of the program (other batches) can
 * rebase onto them without pulling in adapter state.
 */

/** Replace a `snake_case` slug with spaced words (fallback for unknown enums). */
function humanizeToken(token: string): string {
  return token.replace(/_/g, " ").trim();
}

/** Capitalize the first letter of a string. */
function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

/** Full timeline label, e.g. `3_6_months` → "Buying in 3–6 months". */
export function formatTimeline(token?: string | null): string {
  if (!token) return "Timeline not shared";
  switch (token) {
    case "0_3_months":
      return "Buying in 0–3 months";
    case "3_6_months":
      return "Buying in 3–6 months";
    case "6_12_months":
      return "Buying in 6–12 months";
    case "12_plus":
      return "Exploring (12+ months)";
    default:
      return humanizeToken(token);
  }
}

/** Short timeline chip, e.g. `3_6_months` → "3–6 mo". */
export function formatTimelineShort(token?: string | null): string {
  if (!token) return "—";
  switch (token) {
    case "0_3_months":
      return "0–3 mo";
    case "3_6_months":
      return "3–6 mo";
    case "6_12_months":
      return "6–12 mo";
    case "12_plus":
      return "12+ mo";
    default:
      return humanizeToken(token);
  }
}

/** Buyer-type label, e.g. `first_time` → "First-time buyer". */
export function formatBuyerType(token?: string | null): string {
  if (!token) return "—";
  switch (token) {
    case "first_time":
      return "First-time buyer";
    case "move_up":
      return "Move-up buyer";
    case "investor":
      return "Investor";
    case "downsizing":
      return "Downsizing";
    default:
      return capitalize(humanizeToken(token));
  }
}

/** Credit-range label, e.g. `excellent_740+` → "740+". */
export function formatCreditRange(token?: string | null): string {
  if (!token) return "Not shared";
  switch (token) {
    case "excellent_740+":
      return "740+";
    case "good_700_739":
      return "700–739";
    case "fair_660_699":
      return "660–699";
    case "below_660":
      return "Below 660";
    default:
      return humanizeToken(token);
  }
}

/**
 * Price-band label from the backend `priceBand` k-bucket, e.g. `600k-700k` →
 * "$600k–$700k band". The backend emits `<lo>k-<hi>k` (see consumer/dto.go).
 */
export function formatPriceBand(band?: string | null): string {
  if (!band) return "Not shared";
  const parts = band.split("-");
  if (parts.length === 2) {
    return `$${parts[0]}–$${parts[1]} band`;
  }
  return `${band} band`;
}
